import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserGroupIcon, TrophyIcon, PlayIcon, SparklesIcon, ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';
import { useSession } from '../contexts/SessionContext';
import { useAuth } from '../contexts/AuthContext';
import { DefaultService, EnrichedUnclaimedPlayer } from '../generated-api';

const OnboardingPage: React.FC = () => {
  const [unclaimedPlayers, setUnclaimedPlayers] = useState<EnrichedUnclaimedPlayer[]>([]);
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { userInfo } = useSession();
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  useEffect(() => {
    const fetchUnclaimedPlayers = async () => {
      try {
        setLoading(true);
        const players = await DefaultService.getUnclaimedPlayersPlayersUnclaimedGet();
        setUnclaimedPlayers(players);
      } catch (err) {
        console.error('Failed to fetch unclaimed players:', err);
        setError('Failed to load player profiles. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchUnclaimedPlayers();
  }, []);

  const handleClaimProfile = async () => {
    if (!selectedPlayerId) return;

    try {
      setClaiming(true);
      setError(null);

      // Claim the player profile - this associates it with the user's email
      // Uses the regular JWT token for authentication
      await DefaultService.claimPlayerProfilePlayersPlayerIdClaimPost({
        playerId: selectedPlayerId
      });

      // Navigate to home page - user is now fully onboarded
      // The session will refresh and recognize they have a player
      window.location.href = '/';
    } catch (err: any) {
      console.error('Failed to claim player profile:', err);

      // Check if error is because user already has a profile
      if (err.message && err.message.includes('already claimed')) {
        console.log('⚠️ User already has a profile, refreshing session and redirecting to home...');
        // User already has a profile - refresh the session to update onboardingRequired flag
        // Hard reload to re-initialize the session from scratch,
        // avoiding a race with OnboardingRedirect's stale onboardingRequired state.
        window.location.href = '/';
      } else {
        setError(err.message || 'Failed to claim profile. Please try again.');
        setClaiming(false);
      }
    }
  };

  const handleCreateNew = () => {
    navigate('/players/new');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-body text-lg">Loading profiles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Logout Button */}
        <div className="flex justify-end mb-4">
          <button
            onClick={handleLogout}
            className="inline-flex items-center px-4 py-2 border border-edge-input rounded-md shadow-sm text-sm font-medium text-body bg-surface hover:bg-surface-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
          >
            <ArrowRightOnRectangleIcon className="h-5 w-5 mr-2" />
            Logout
          </button>
        </div>

        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center p-2 bg-primary-100 rounded-full mb-4">
            <SparklesIcon className="h-8 w-8 text-primary-600" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-heading mb-4">
            Welcome, {userInfo?.name || 'Player'}!
          </h1>
          <p className="text-xl text-body max-w-2xl mx-auto">
            Join the ultimate board game community. Choose your existing profile or create a new one to start tracking your victories!
          </p>
        </div>

        {error && (
          <div className="mb-8 bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Existing Players Section */}
        {unclaimedPlayers.length > 0 && (
          <div className="mb-12">
            <h2 className="text-fluid-h2 font-bold text-heading mb-6 flex items-center">
              <UserGroupIcon className="h-7 w-7 text-primary-600 mr-2" />
              Is this you? Claim your profile
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {unclaimedPlayers.map((player) => (
                <div
                  data-track="onboarding_player_card"
                  key={player.id}
                  onClick={() => setSelectedPlayerId(player.id)}
                  className={`relative bg-surface rounded-xl shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden ${
                    selectedPlayerId === player.id
                      ? 'ring-2 ring-primary-500 transform scale-105'
                      : 'hover:scale-102'
                  }`}
                >
                  {/* Selection Indicator */}
                  {selectedPlayerId === player.id && (
                    <div className="absolute top-2 right-2 z-10">
                      <div className="bg-primary-600 rounded-full p-0.5">
                        <svg className="h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                  )}

                  {/* Player Card Content */}
                  <div className="p-3">
                    {/* Avatar */}
                    <div className="flex justify-center mb-2">
                      {player.imageUrl ? (
                        <img
                          src={player.imageUrl}
                          alt={player.name}
                          className="h-12 w-12 rounded-full object-cover border-2 border-edge-subtle"
                        />
                      ) : (
                        <div className="h-12 w-12 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center border-2 border-edge-subtle">
                          <span className="text-lg font-bold text-white">
                            {player.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Player Name */}
                    <h3 className="text-sm font-bold text-heading text-center mb-2 truncate">
                      {player.name}
                    </h3>

                    {/* Stats Grid */}
                    <div className="space-y-1.5">
                      {/* Global Rating */}
                      <div className="flex items-center justify-between bg-gradient-to-r from-yellow-50 to-orange-50 rounded px-2 py-1.5">
                        <div className="flex items-center">
                          <TrophyIcon className="h-3 w-3 text-yellow-600 mr-1" />
                          <span className="text-xs font-medium text-body">Global rating</span>
                        </div>
                        <span className="text-sm font-bold text-heading">
                          {player.globalRating ? player.globalRating.rating.toFixed(2) : 'New'}
                        </span>
                      </div>

                      {/* Leagues */}
                      <div className="flex items-center justify-between bg-gradient-to-r from-primary-50 to-primary-100 rounded px-2 py-1.5">
                        <div className="flex items-center">
                          <UserGroupIcon className="h-3 w-3 text-primary-600 mr-1" />
                          <span className="text-xs font-medium text-body">Leagues</span>
                        </div>
                        <span className="text-sm font-bold text-heading">
                          {player.leagueCount}
                        </span>
                      </div>

                      {/* Plays */}
                      <div className="flex items-center justify-between bg-gradient-to-r from-green-50 to-emerald-50 rounded px-2 py-1.5">
                        <div className="flex items-center">
                          <PlayIcon className="h-3 w-3 text-green-600 mr-1" />
                          <span className="text-xs font-medium text-body">Plays</span>
                        </div>
                        <span className="text-sm font-bold text-heading">
                          {player.playCount}
                        </span>
                      </div>

                      {/* Last Played */}
                      <div className="flex items-center justify-between bg-gradient-to-r from-purple-50 to-pink-50 rounded px-2 py-1.5">
                        <div className="flex items-center">
                          <svg className="h-3 w-3 text-purple-600 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                          </svg>
                          <span className="text-xs font-medium text-body">Last played</span>
                        </div>
                        <span className="text-xs font-semibold text-heading">
                          {(player as any).lastPlayDate
                            ? new Date((player as any).lastPlayDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                            : 'Never'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Claim Button */}
            <div className="mt-8 flex justify-center">
              <button
                onClick={handleClaimProfile}
                disabled={!selectedPlayerId || claiming}
                className={`px-8 py-4 rounded-xl text-lg font-semibold transition-all duration-300 ${
                  selectedPlayerId && !claiming
                    ? 'bg-primary-700 text-white hover:bg-primary-800 hover:shadow-lg transform hover:-translate-y-1'
                    : 'bg-gray-300 text-muted cursor-not-allowed'
                }`}
              >
                {claiming ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Claiming Profile...
                  </span>
                ) : (
                  'Claim Selected Profile'
                )}
              </button>
            </div>
          </div>
        )}

        {/* Divider */}
        <div className="relative my-12">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-edge-input"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-6 bg-gradient-to-br from-primary-50 via-white to-primary-100 text-muted font-medium">
              OR
            </span>
          </div>
        </div>

        {/* Create New Profile Section */}
        <div className="text-center">
          <h2 className="text-fluid-h2 font-bold text-heading mb-4">
            New to the game?
          </h2>
          <p className="text-body mb-6 max-w-md mx-auto">
            Start fresh with a brand new profile and begin your journey to board game greatness!
          </p>
          <button
            onClick={handleCreateNew}
            disabled={claiming}
            className="px-8 py-4 bg-surface text-primary-700 border-2 border-primary-600 rounded-xl text-lg font-semibold hover:bg-primary-50 transition-all duration-300 hover:shadow-lg transform hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Create New Profile
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
